import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Diagnostic } from '@ionic-native/diagnostic/ngx';
import { InAppBrowser, InAppBrowserOptions } from '@ionic-native/in-app-browser/ngx';
import { AlertController, LoadingController, Platform } from '@ionic/angular';
import {
  BASE_URL_PATH,
  IMG_200,
  NO_IMG_URL,
  SPICE_KITCHEN_IMG_PROCESS,
  SQUARE_PROCESSOR,
  STONE_CREEK_MERCHANT_ID
} from '../constants';
import { PermissionStatus, Platforms } from '../enums/util.enum';
import { Coordinate, RestaurantAddress } from '../models/restaurant.model';
import { RouteService } from './route.service';
import { StorageService } from './storage.service';
import { environment } from '../../../environments/environment';
import { URL } from '../enums/url.enum';
import {Observable, Observer} from 'rxjs';

declare let launchnavigator;
@Injectable({
  providedIn: 'root',
})
export class UtilService {
  public isLoaderActive: boolean = false;
  public isAlertActive: boolean = false;
  private options: InAppBrowserOptions = {
    hidenavigationbuttons: 'yes',
    location: 'no',
  };

  constructor(
    private loadingController: LoadingController,
    private diagnostic: Diagnostic,
    private router: Router,
    private iab: InAppBrowser,
    private alertController: AlertController,
    private routeService: RouteService,
    private storageService: StorageService,
    private platform: Platform
  ) {}

  public getImageUrl(url: string, imgParam: string = IMG_200, isSpiceKitchen: boolean): string {
    const spiceKitchen = SPICE_KITCHEN_IMG_PROCESS + SQUARE_PROCESSOR + imgParam +  url;
    const umgUrl = isSpiceKitchen ? spiceKitchen : SQUARE_PROCESSOR + imgParam +  url;
    if (environment.apiUrl.includes('sandbox')) {
      return url ? url : NO_IMG_URL;
    }
    return url ? umgUrl : NO_IMG_URL;
  }

  public getDistanceBetweenTwoPoints(cord1: Coordinate, cord2: Coordinate): any {
    if (cord1.latitude == cord2.latitude && cord1.longitude == cord2.longitude) {
      return 0;
    }
    const radlatitude1 = (Math.PI * cord1.latitude) / 180;
    const radlatitude2 = (Math.PI * cord2.latitude) / 180;
    const theta = cord1.longitude - cord2.longitude;
    const radtheta = (Math.PI * theta) / 180;
    let dist = Math.sin(radlatitude1) * Math.sin(radlatitude2) + Math.cos(radlatitude1) * Math.cos(radlatitude2) * Math.cos(radtheta);
    if (dist > 1) {
      dist = 1;
    }
    dist = Math.acos(dist);
    dist = (dist * 180) / Math.PI;
    dist = dist * 60 * 1.1515;
    return dist.toFixed(2);
  }

  public async activeLoader(message: string): Promise<void> {
    this.dismissActiveLoader();
    const loading = await this.loadingController.create({
      cssClass: 'loader',
      message,
      duration: 30000,
    });
    this.isLoaderActive = true;
    await loading.present();
  }

  public async dismissActiveLoader(): Promise<boolean> {
    try {
      if (this.isLoaderActive) {
        this.isLoaderActive = false;
        await this.loadingController.dismiss();
      }
    } catch (err) {
      return false;
    }
  }

  public async presentAlert(header: string, message: string, restaurantClosed = true, merchantId = null, locationId = null): Promise<void> {
    this.isAlertActive = true;
    const alert = await this.alertController.create({
      cssClass: 'my-custom-class',
      header,
      message,
      buttons: restaurantClosed
        ? [
            {
              text: 'Cancel',
              role: 'cancel',
              cssClass: 'secondary',
              handler: () => {},
            },
            {
              text: 'All Locations',
              handler: () => {
                this.navigateToLocationPage();
              },
            },
          ]
        : [
            {
              text: 'Cancel',
              role: 'cancel',
              cssClass: 'secondary',
              handler: () => {},
            },
            {
              text: 'Ok',
              handler: () => {
                this.router.navigate(['merchants', merchantId, 'locations', locationId, 'checkout']);
              },
            },
          ],
    });
    await alert.present();
  }

  public async dismissAlert(): Promise<boolean> {
    try {
      if (this.isAlertActive) {
        this.isAlertActive = false;
        await this.alertController.dismiss();
      }
    } catch (err) {
      return false;
    }
  }

  public isStoneCreek(): boolean {
    return this.routeService.getMerchantId() === STONE_CREEK_MERCHANT_ID;
  }

  public async getLocationAuthState(): Promise<number> {
    try {
      const state = await this.diagnostic.getLocationAuthorizationStatus();
      switch (state) {
        case this.diagnostic.permissionStatus.NOT_REQUESTED:
          return PermissionStatus.GRANTED;
        case this.diagnostic.permissionStatus.DENIED_ALWAYS:
          return PermissionStatus.NOT_REQUESTED;
        case this.diagnostic.permissionStatus.GRANTED:
          return PermissionStatus.DENIED_ALWAYS;
        case this.diagnostic.permissionStatus.GRANTED_WHEN_IN_USE:
          return PermissionStatus.DENIED_ALWAYS;
      }
    } catch (error) {
      return PermissionStatus.DENIED_ALWAYS;
    }
  }

  public async requestLocationAuth(): Promise<boolean> {
    try {
      const permission = await this.diagnostic.requestLocationAuthorization(this.diagnostic.locationAuthorizationMode.WHEN_IN_USE);
      if (permission === this.diagnostic.permissionStatus.GRANTED || permission === this.diagnostic.permissionStatus.GRANTED_WHEN_IN_USE) {
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }

  public getCurrentAuthState(): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      try {
        const permissionStatus = await this.getLocationAuthState();
        if (permissionStatus === PermissionStatus.GRANTED) {
          const isLocationEnabled = await this.isLocationEnabled();
          const response = isLocationEnabled ? await this.requestLocationAuth() : false;
          resolve(response);
        } else {
          resolve(false);
        }
      } catch (error) {
        resolve(true);
      }
    });
  }

  public getCurrentLocation(): Observable<Position> {
    return new Observable((observer: Observer<Position>) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            observer.next(position);
            observer.complete();
          },
          (error) => {
            observer.error(error);
          }
        );
      } else {
        observer.error('Геолокация не поддерживается в вашем браузере');
      }
    });
  }

  private isLocationEnabled(): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await this.diagnostic.isLocationEnabled();
        resolve(response);
      } catch (err) {
        resolve(false);
      }
    });
  }

  public openInAppBrowser(url: string): void {
    this.iab.create(url, '_blank', this.options);
  }

  public navigateToLocationPage(): void {
    this.router.navigate([BASE_URL_PATH, STONE_CREEK_MERCHANT_ID]);
  }

  public async navigateToCheckout(): Promise<void> {
    const currentRestaurantId = await this.storageService.get('currentRestaurantId');
    const baseUrl = this.routeService.getBaseUrl();
    await this.router.navigate([baseUrl, 'locations', currentRestaurantId, 'checkout']);
  }

  public navigateToRewards(): void {
    this.router.navigate([BASE_URL_PATH, STONE_CREEK_MERCHANT_ID, 'rewards']);
  }

  public navigateToHome(): void {
    this.router.navigate([BASE_URL_PATH, STONE_CREEK_MERCHANT_ID, 'home-screen']);
  }

  public openLocationInMaps(restaurantAddress: RestaurantAddress): void {
    const destinationLocation = `${restaurantAddress.addressLine}, ${restaurantAddress.locality}, ${restaurantAddress.postalCode}, ${restaurantAddress.country}`;
    if (!this.platform.is(Platforms.CORDOVA)) {
      const newTab = window.open(`https://www.google.com/maps/place/${destinationLocation}`, '_blank');
      newTab.focus();
    } else {
      launchnavigator.navigate(destinationLocation);
    }
  }
}
